import "@/App.css";
import { useState, useEffect } from "react";
import { ArrowDown, ArrowRight, ShieldCheck, Infinity as InfinityIcon, TrendingUp, Sparkles, Globe, Share2 } from "lucide-react";
import { PowrForm } from "@/components/PowrForm";

const LOGO = "/smartlock-logo.png";
const SHYFT_LOGO = "/sl-logo.jpg";
const JOIN_URL = "https://shyftcoalition.com/?autojoin=1";

const TM = ({ className = "" }) => (
  <sup className={`align-super text-[0.42em] font-semibold tracking-normal ${className}`}>™</sup>
);

// SmartLock wordmark — always capital S + L, with a tiny ™.
const SmartLock = ({ className = "", tmClassName = "" }) => (
  <span className={`whitespace-nowrap ${className}`}>
    SmartLock<TM className={tmClassName} />
  </span>
);

// SHYFT Coalition™ — ™ sits behind "Coalition" and inherits the word's color.
const ShyftCoalition = ({ className = "" }) => (
  <span className={`whitespace-nowrap ${className}`}>
    SHYFT Coalition<TM />
  </span>
);

const VISIT_URL = "https://shyftasap.com/";

const Brand = ({ className = "" }) => (
  <a href="/" data-testid="brand-logo" className={`flex items-center gap-2.5 ${className}`}>
    <img
      src={LOGO}
      alt="SmartLock"
      className="h-10 w-10 object-contain"
    />
    <span className="font-display text-xl font-semibold tracking-tight text-[var(--sl-ink)]">
      <SmartLock />
    </span>
  </a>
);

const Nav = () => (
  <header className="sticky top-0 z-40 border-b border-[var(--sl-ink)]/10 bg-[var(--sl-paper)]/80 backdrop-blur-xl">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
      <Brand />
      <a
        href="#consultation"
        data-testid="nav-consultation-button"
        className="inline-flex items-center gap-2 rounded-full btn-red px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:shadow-[var(--sl-red)]/30"
      >
        Request Consultation
      </a>
    </div>
  </header>
);

const Pill = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-[var(--sl-ink)]/10 bg-white px-3.5 py-1.5 text-xs font-semibold text-[var(--sl-ink)]/80 shadow-sm">
    <Icon className="h-3.5 w-3.5 text-[var(--sl-red)]" />
    {children}
  </span>
);

const Hero = () => (
  <section className="relative overflow-hidden">
    {/* ambient glows + engraving */}
    <div className="pointer-events-none absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-[var(--sl-red)]/15 blur-3xl" />
    <div className="pointer-events-none absolute top-1/3 right-0 h-[26rem] w-[26rem] rounded-full bg-[var(--sl-blue)]/12 blur-3xl" />
    <div className="pointer-events-none absolute left-1/2 top-0 h-[34rem] w-[60rem] -translate-x-1/2 bg-guilloche opacity-70" />
    <div className="absolute inset-0 bg-grid opacity-70" />

    <div className="relative mx-auto max-w-3xl px-6 pt-16 text-center lg:pt-24">
      <div className="animate-rise" style={{ animationDelay: "0s" }}>
        <span className="inline-flex items-center rounded-full border border-[var(--sl-blue)]/25 bg-white/80 px-4 py-1.5 text-[11px] font-bold tracking-[0.16em] text-[var(--sl-ink)] glow-gold">
          <span className="uppercase">SHYFT</span>&nbsp;Financial Ecosystem Cornerstone
        </span>
      </div>

      <h1
        className="font-display mx-auto mt-6 max-w-2xl font-semibold leading-[1.06] tracking-tight text-[var(--sl-ink)] animate-rise"
        style={{ animationDelay: "0.08s" }}
      >
        <span className="block text-red-gradient text-5xl sm:text-6xl lg:text-7xl">
          <SmartLock tmClassName="[-webkit-text-fill-color:#c43f3c] text-[#c43f3c]" />
        </span>
        <span className="mt-3 block text-2xl text-[var(--sl-ink)]/90 sm:text-3xl lg:text-4xl">
          The Home of <span className="italic">‘Forever Money’</span>
        </span>
      </h1>

      <p
        className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[var(--sl-ink)]/70 animate-rise sm:text-lg [text-wrap:balance]"
        style={{ animationDelay: "0.16s" }}
      >
        A highly flexible financial tool that lets you earn dividends and guaranteed
        interest — for the rest of your life — on the money you’re spending today.
      </p>

      <div
        className="mt-8 flex flex-wrap items-center justify-center gap-3 animate-rise"
        style={{ animationDelay: "0.24s" }}
      >
        <a
          href="#consultation"
          data-testid="hero-consultation-cta"
          className="group inline-flex items-center gap-2 rounded-full btn-red px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[var(--sl-red)]/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[var(--sl-red)]/40"
        >
          Request Your <SmartLock /> Consultation
          <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
        </a>
      </div>

      <div
        className="mt-9 flex flex-wrap justify-center gap-2.5 animate-rise"
        style={{ animationDelay: "0.32s" }}
      >
        <Pill icon={InfinityIcon}>Income for Life</Pill>
        <Pill icon={TrendingUp}>Dividends + Guaranteed Interest</Pill>
        <Pill icon={ShieldCheck}>Flexible &amp; Secure</Pill>
      </div>
    </div>

    {/* Full-width form below the header */}
    <div
      id="consultation"
      className="relative mx-auto mt-14 max-w-3xl scroll-mt-24 px-6 pb-20 animate-rise lg:mt-16"
      style={{ animationDelay: "0.32s" }}
    >
      <div className="relative">
        <div className="pointer-events-none absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[var(--sl-red)]/20 via-transparent to-[var(--sl-blue)]/15 blur-xl" />
        <div className="relative rounded-[1.75rem] border border-[var(--sl-ink)]/10 bg-white p-6 shadow-2xl shadow-[var(--sl-ink)]/10 sm:p-8 lg:p-10">
          <div className="mb-6 flex items-center gap-3 border-b border-[var(--sl-ink)]/10 pb-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl btn-red text-white shadow-sm shadow-[var(--sl-red)]/30">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-xl font-bold text-black sm:text-2xl">
                <SmartLock /> Consultation Form
              </h2>
              <p className="mt-1 text-xs text-[var(--sl-ink)]/60 sm:text-sm">
                Once you submit this form, someone will contact you to schedule your consultation.
              </p>
            </div>
          </div>
          <PowrForm />
        </div>
      </div>
    </div>
  </section>
);

const RSVP_URL = "https://shyftcoalition.com/spaces/20736990/events";
const ZOOM_URL = "https://us06web.zoom.us/j/2480049812";

function etOffsetMinutes(date) {
  try {
    const s = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      timeZoneName: "shortOffset",
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName").value;
    const m = s.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/);
    if (m) {
      const hrs = parseInt(m[1], 10);
      const mins = m[2] ? parseInt(m[2], 10) : 0;
      return hrs * 60 + (hrs < 0 ? -mins : mins);
    }
  } catch (e) {}
  return -300;
}

// Next Tuesday 8:00 PM ET (America/New_York, DST-aware) as a real Date.
function nextTuesday8pmET() {
  const now = new Date();
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      hour12: false,
    })
      .formatToParts(now)
      .map((p) => [p.type, p.value])
  );
  const wdMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const wd = wdMap[parts.weekday];
  const h = parseInt(parts.hour, 10) % 24;
  let add = (2 - wd + 7) % 7;
  if (add === 0 && h >= 21) add = 7; // Tuesday but class already ended
  const base = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day));
  base.setUTCDate(base.getUTCDate() + add);
  const ty = base.getUTCFullYear();
  const tm = base.getUTCMonth() + 1;
  const td = base.getUTCDate();
  const off = etOffsetMinutes(new Date(Date.UTC(ty, tm - 1, td, 20, 0, 0)));
  return new Date(Date.UTC(ty, tm - 1, td, 20, 0, 0) - off * 60000);
}

const LiveClass = () => {
  const [target, setTarget] = useState(() => nextTuesday8pmET());
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const startMs = target.getTime();
  const endMs = startMs + 60 * 60 * 1000; // class runs ~1 hour
  const isLive = now >= startMs && now < endMs;

  useEffect(() => {
    if (now >= endMs) setTarget(nextTuesday8pmET());
  }, [now, endMs]);

  const diff = Math.max(0, startMs - now);
  const tiles = [
    { v: Math.floor(diff / 86400000), l: "Days" },
    { v: Math.floor((diff % 86400000) / 3600000), l: "Hrs" },
    { v: Math.floor((diff % 3600000) / 60000), l: "Min" },
    { v: Math.floor((diff % 60000) / 1000), l: "Sec" },
  ];
  const nextLabel = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(target);

  const times = [
    { z: "ET", t: "8pm", active: true },
    { z: "CT", t: "7pm" },
    { z: "MT", t: "6pm" },
    { z: "PT", t: "5pm" },
  ];

  const share = async () => {
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({ title: "SmartLock™ — The Home of ‘Forever Money’", url });
        return;
      }
    } catch (e) {}
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {}
  };

  return (
    <section data-testid="live-class-section" className="relative overflow-hidden text-[var(--sl-ink)]">
      <div className="pointer-events-none absolute left-1/3 top-8 h-80 w-80 -translate-x-1/2 rounded-full bg-[var(--sl-red)]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-10 bottom-10 h-72 w-72 rounded-full bg-[var(--sl-gold-2)]/14 blur-3xl" />
      <div className="absolute inset-0 bg-grid opacity-50" />

      <div className="relative mx-auto max-w-3xl px-6 py-20 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[var(--sl-ink)]/50">
          Live Class · Community
        </p>
        <h2 className="font-display mt-4 text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
          Join a Live Class
          <br />
          <span className="text-gold-gradient">Step Inside Our Community</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-[var(--sl-ink)]/70 sm:text-base [text-wrap:balance]">
          Attend the live <SmartLock /> class to see how ‘Forever Money’ works and get your
          questions answered in real time — then join our community to keep the conversation going.
        </p>

        <div className="relative mt-10 rounded-3xl border border-[var(--sl-ink)]/10 bg-white p-6 shadow-xl shadow-[var(--sl-ink)]/5 sm:p-8">
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <span className="text-sm font-semibold italic text-[var(--sl-gold)]">Class Times:</span>
            {times.map((t) => (
              <span
                key={t.z}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold ${
                  t.active
                    ? "bg-gradient-to-r from-[#b9821f] to-[#e6b94e] text-[var(--sl-ink)] shadow-sm"
                    : "border border-[var(--sl-ink)]/15 text-[var(--sl-ink)]/70"
                }`}
              >
                {t.t} {t.z}
              </span>
            ))}
          </div>

          {isLive ? (
            <div className="mt-9 flex items-center justify-center gap-3" data-testid="class-in-session">
              <span className="relative flex h-3.5 w-3.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--sl-blue)] opacity-75" />
                <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-[var(--sl-blue)]" />
              </span>
              <span className="text-lg font-bold text-[var(--sl-blue-deep)] sm:text-xl">
                Class in Session — <SmartLock /> Class
              </span>
            </div>
          ) : (
            <>
              <p
                data-testid="next-class-label"
                className="mt-7 text-lg font-bold text-glow-blue sm:text-xl"
              >
                Next Class: {nextLabel} @ 8:00 PM ET
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 sm:gap-4">
                <span className="hidden text-sm font-semibold italic text-[var(--sl-gold)] sm:inline">
                  Starts in
                </span>
                {tiles.map((t) => (
                  <div key={t.l} className="flex flex-col items-center">
                    <div className="flex h-16 w-14 items-center justify-center rounded-xl border border-[var(--sl-ink)]/10 bg-[var(--sl-paper-2)] font-display text-2xl font-bold text-red-gradient sm:h-20 sm:w-16 sm:text-3xl">
                      {String(t.v).padStart(2, "0")}
                    </div>
                    <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--sl-ink)]/40">
                      {t.l}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {isLive ? (
            <a
              href={ZOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="join-now-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--sl-blue)] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[var(--sl-blue)]/40 ring-2 ring-[var(--sl-blue)]/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[var(--sl-blue-deep)]"
            >
              Join Now <ArrowRight className="h-4 w-4" />
            </a>
          ) : (
            <a
              href={RSVP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="join-class-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--sl-ink)] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[var(--sl-ink)]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[var(--sl-ink-soft)]"
            >
              Join Class <ArrowRight className="h-4 w-4" />
            </a>
          )}
          <a
            href={JOIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="live-join-community-button"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#b9821f] to-[#e6b94e] px-6 py-3.5 text-sm font-bold text-[var(--sl-ink)] transition-all duration-300 hover:-translate-y-0.5"
          >
            <Sparkles className="h-4 w-4" /> Join Our Community
          </a>
          <button
            onClick={share}
            data-testid="share-button"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(10,21,50,0.3)] bg-white px-6 py-3 text-sm font-bold text-[var(--sl-ink)] shadow-md shadow-[rgba(10,21,50,0.12)] transition-colors duration-300 hover:border-[rgba(10,21,50,0.55)] hover:bg-[rgba(10,21,50,0.05)]"
          >
            {copied ? "Link Copied!" : "Share"} <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="relative bg-[var(--sl-ink)] text-white">
    <div className="rule-gold" />
    <div className="mx-auto max-w-3xl px-6 py-14 text-center">
      <a
        href="https://shyftcoalition.com/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="footer-brand-logo"
        className="inline-flex flex-col items-center gap-3"
      >
        <img
          src={SHYFT_LOGO}
          alt="SHYFT Coalition"
          className="h-16 w-16 rounded-2xl object-contain ring-1 ring-[var(--sl-gold)]/40 shadow-lg shadow-black/30"
        />
        <span className="font-display text-xl font-semibold tracking-tight text-white">
          <ShyftCoalition />
        </span>
      </a>

      <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-white/60">
        The <ShyftCoalition />. A private network for people who believe in
        participating in their own rescue.
      </p>

      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        <a
          href={VISIT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-visit-button"
          className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
        >
          <Globe className="h-4 w-4 text-[var(--sl-gold-2)]" />
          Visit Our Website
        </a>
        <a
          href={JOIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-join-button"
          className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#b9821f] to-[#e6b94e] px-5 py-2.5 text-sm font-bold text-[var(--sl-ink)] transition-transform duration-300 hover:-translate-y-0.5"
        >
          <Sparkles className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
          Join Our Community
        </a>
      </div>

      <p className="mt-9 text-sm text-white/55">
        Need a human? Reach the concierge directly at{" "}
        <a
          href="mailto:support@shyftcoalition.com"
          data-testid="footer-concierge-email"
          className="font-semibold text-[var(--sl-gold-2)] underline-offset-4 hover:underline"
        >
          support@shyftcoalition.com
        </a>
      </p>

      <div className="mt-8 border-t border-white/10 pt-6">
        <p className="text-xs text-white/45">This webpage is sponsored by the <ShyftCoalition />.</p>
        <p className="mt-2 text-xs text-white/45">© 2026 <ShyftCoalition /> All Rights Reserved.</p>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App grain" data-testid="landing-page">
      <div className="rule-gold" />
      <Nav />
      <main>
        <Hero />
      </main>
      {/* elegant separator between the form and the live-class section */}
      <div data-testid="section-separator" className="mx-auto flex max-w-2xl items-center gap-4 px-6 py-2">
        <span className="h-0.5 flex-1 rounded-full bg-gradient-to-r from-transparent via-[var(--sl-gold)]/70 to-[var(--sl-gold)]" />
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--sl-gold)]/60 bg-white shadow-md shadow-[var(--sl-gold)]/20">
          <ShieldCheck className="h-4 w-4 text-[var(--sl-red)]" />
        </span>
        <span className="h-0.5 flex-1 rounded-full bg-gradient-to-l from-transparent via-[var(--sl-gold)]/70 to-[var(--sl-gold)]" />
      </div>
      <LiveClass />
      {/* gradual cream -> navy transition into the SHYFT footer */}
      <div
        aria-hidden
        className="h-28 w-full"
        style={{ background: "linear-gradient(180deg, rgba(10,21,50,0) 0%, rgba(10,21,50,0.55) 55%, var(--sl-ink) 100%)" }}
      />
      <Footer />
    </div>
  );
}

export default App;
